import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Layers, GitCompare, Search, Gamepad2, Lock, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GAME_TYPES = [
  {
    id: 'flashcards',
    title: 'Flashcards',
    description: 'Review concepts with flip cards. Question on front, answer and rationale on back.',
    icon: Layers,
    color: 'bg-blue-500',
    bgLight: 'bg-blue-50',
    textColor: 'text-blue-700'
  },
  {
    id: 'matching',
    title: 'Matching',
    description: 'Match related concepts like drug-mechanism pairs or condition-features.',
    icon: GitCompare,
    color: 'bg-purple-500',
    bgLight: 'bg-purple-50',
    textColor: 'text-purple-700'
  },
  {
    id: 'wordsearch',
    title: 'Word Search',
    description: 'Find medical terms hidden in a puzzle grid. Keywords from answers and rationales.',
    icon: Search,
    color: 'bg-emerald-500',
    bgLight: 'bg-emerald-50',
    textColor: 'text-emerald-700'
  }
];

export default function Games() {
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState(null);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/subscription/status`, { withCredentials: true });
        const data = res.data;
        
        // Admins, Pro users, and Beta users have access
        const isAdmin = data.isAdmin;
        const isPro = data.currentPlan?.toLowerCase() === 'pro';
        const isBeta = data.currentPlan?.toLowerCase() === 'beta';
        
        setHasAccess(isAdmin || isPro || isBeta);
        setCurrentPlan(data.currentPlan);
      } catch (error) {
        console.error('Error checking access:', error);
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkAccess();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show upgrade prompt for Basic users
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="hover:bg-slate-100 text-slate-600 hover:text-slate-900 mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
        
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Games Require Pro Subscription</h1>
          <p className="text-slate-600 mb-8 max-w-md mx-auto">
            Learning games including Flashcards, Matching, and Word Search are exclusive to Pro members. 
            Upgrade your subscription to unlock these powerful study tools.
          </p>
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 mb-8 border border-amber-200">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Crown className="w-5 h-5 text-amber-600" />
              <span className="font-semibold text-slate-900">Pro Plan Benefits</span>
            </div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li>✓ All learning games (Flashcards, Matching, Word Search)</li>
              <li>✓ Unlimited case studies</li>
              <li>✓ Reference sheets PDF download</li>
              <li>✓ Priority support</li>
            </ul>
          </div>
          <Button
            onClick={() => navigate('/pricing')}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8"
          >
            <Crown className="w-4 h-4 mr-2" />
            Upgrade to Pro
          </Button>
          <p className="text-sm text-slate-500 mt-4">
            Currently on: <span className="font-medium capitalize">{currentPlan || 'Basic'}</span> plan
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="hover:bg-slate-100 text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-primary to-accent rounded-lg">
              <Gamepad2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-sans">Learning Games</h1>
              <p className="text-slate-600 mt-1">Reinforce your knowledge through interactive games</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-3 gap-6">
          {GAME_TYPES.map((game) => {
            const Icon = game.icon;
            return (
              <button
                key={game.id}
                onClick={() => navigate(`/games/${game.id}/setup`)}
                className="bg-white rounded-xl border border-slate-200 p-6 text-left hover:shadow-lg hover:border-slate-300 transition-all group"
              >
                <div className={`w-14 h-14 ${game.bgLight} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className={`w-7 h-7 ${game.textColor}`} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{game.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{game.description}</p>
                <div className="mt-4 flex items-center text-sm font-medium text-primary">
                  Play Now
                  <ArrowLeft className="w-4 h-4 ml-1 rotate-180" />
                </div>
              </button>
            );
          })}
        </div>

        {/* Info Section */}
        <div className="mt-12 bg-slate-50 rounded-xl p-6 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-3">How Games Help You Learn</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-slate-600">
            <div>
              <strong className="text-slate-900">Active Recall</strong>
              <p>Flashcards test your memory without hints, strengthening neural pathways.</p>
            </div>
            <div>
              <strong className="text-slate-900">Pattern Recognition</strong>
              <p>Matching games build associations between related clinical concepts.</p>
            </div>
            <div>
              <strong className="text-slate-900">Vocabulary Building</strong>
              <p>Word search reinforces terminology and spelling of medical terms.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
